import React, { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { DNABox, DNAText, DNATextProps } from '@alucio/lux-ui';

const S = StyleSheet.create({
  excerptContent: {
    overflowWrap: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
    maxWidth: '100%',
  },
})

interface RenderExcerptProps {
  pageNumber?: number;
  textProps: DNATextProps;
  text: string | null | undefined;
}

export const RenderExcerpt: React.FC<RenderExcerptProps> = React.memo((props) => {
  const { pageNumber, textProps, text } = props;

  // Function to parse the HTML and safely render both text and bold elements
  // Created to prevent XSS attacks by rendering only specific tags
  const renderParsedHTMLWithBoldTags = useCallback((htmlString: string): JSX.Element[] => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');

      // Helper function to process nodes recursively
      const processNode = (node: Node, keyPrefix: string): JSX.Element | string | JSX.Element[] => {
        if (node.nodeType === Node.TEXT_NODE) {
          const textContent = node.textContent || '';
          const parts = textContent.split('\n');
          return parts.map((part, partIndex) => (
            <>
              <span key={`${keyPrefix}-${partIndex}`}>{part}</span>
              {partIndex < parts.length - 1 && <br key={`${keyPrefix}-${partIndex}-br`} />}
            </>
          ));
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as HTMLElement;
          const tagName = element.tagName.toLowerCase();

          // Process all child nodes recursively
          const children = Array.from(element.childNodes).map((child, idx) =>
            processNode(child, `${keyPrefix}-${idx}`),
          );

          switch (tagName) {
            case 'b':
            case 'strong':
              return <strong key={keyPrefix}>{children}</strong>;
            case 'i':
            case 'em':
              return <i key={keyPrefix}>{children}</i>;
            case 'u':
              return <u key={keyPrefix}>{children}</u>;
            case 'br':
              return <br key={keyPrefix} />;
            default:
              // Wrap children in a fragment to return a single element
              return <>{children}</>;
          }
        }

        return <span key={keyPrefix} />;
      };

      // Wrap the mapped nodes in fragments to ensure we always return Elements
      return Array.from(doc.body.childNodes).map((node, index) => (
        <React.Fragment key={`root-${index}`}>
          {processNode(node, `root-${index}`)}
        </React.Fragment>
      ));
    } catch (error) {
      console.error('Failed to parse HTML:', error);
      return [<span key="error">Error parsing content</span>];
    }
  }, []);

  // Function to clean the text and add page number if available
  const renderContent = useCallback(
    (text: string | null | undefined, pageNumber?: number): JSX.Element => {
      const cleanedText = (text || '').trim().replace(/\n\s+/g, ' ');
      const contentWithPageNumber = cleanedText && pageNumber
        ? `Slide ${pageNumber} - ${cleanedText}`
        : cleanedText;

      return (
        <div style={S.excerptContent}>
          {renderParsedHTMLWithBoldTags(contentWithPageNumber)}
        </div>
      );
    },
    [renderParsedHTMLWithBoldTags], // Dependencies are correctly set
  );

  return (
    <DNABox appearance="row">
      <DNAText {...textProps} numberOfLines={textProps.numberOfLines} testID={textProps.testID}>
        {renderContent(text, pageNumber)}
      </DNAText>
    </DNABox>
  );
});

export default RenderExcerpt;
